import { useState } from 'react';

import { Card, IconButton, JsonField, NumberField, PropRow, SelectField, TextField, Toggle } from '../ui/controls.js';
import { EffectsEditor } from './EffectsEditor.js';
import { NotesTable } from './NotesTable.js';
import { docVariables, ParamField } from './ParamField.js';
import { SchemaFields, withField } from './SchemaFields.js';
import { VoicePicker } from './VoiceEditor.js';
import { isNum, isObj } from './param-values.js';
import { DEFAULT_SOURCES, SOURCE_FIELDS, SOURCE_LABELS, SOURCE_TYPES, type SourceType } from './sound-catalog.js';
import { previewDoc, previewDocument, stopPreview } from './sound-api.js';
import './sound-editors.css';

type Obj = Record<string, unknown>;

/** Names of the document's instruments (for `instrument` sources and tracks). */
export function instrumentNames(doc: unknown): string[] {
  return isObj(doc) && isObj(doc.instruments) ? Object.keys(doc.instruments) : [];
}

/** Names of `master.returns` buses (send targets). */
export function returnNames(doc: unknown): string[] {
  return isObj(doc) && isObj(doc.master) && isObj(doc.master.returns) ? Object.keys(doc.master.returns) : [];
}

/** Switches a source to another type, keeping pitch fields that both types share. */
export function changeSourceType(src: Obj, type: SourceType): Obj {
  const next: Obj = structuredClone(DEFAULT_SOURCES[type]);
  const keep = new Set(SOURCE_FIELDS[type].map((f) => f.key));
  for (const k of ['note', 'freq']) if (keep.has(k) && src[k] !== undefined) {
    delete next.note;
    delete next.freq;
    next[k] = src[k];
  }
  if (src.seed !== undefined) next.seed = src.seed;
  return next;
}

/* ------------------------------------------------------------------ */
/* Structured source fields                                            */

function OperatorList({ value, onChange, vars }: { value: unknown; onChange: (v: unknown) => void; vars: Record<string, number> }) {
  const ops: Obj[] = Array.isArray(value) ? value.map((o) => (isObj(o) ? o : {})) : [];
  const set = (i: number, k: string, v: unknown) => onChange(ops.map((o, j) => (j === i ? withField(o, k, v) : o)));
  return (
    <div className="snd-list">
      {ops.map((op, i) => (
        <div key={i} className="snd-subcard">
          <div className="snd-row">
            <span className="snd-mini-label">{`Op ${i + 1}`}</span>
            <NumberField label="Ratio" value={isNum(op.ratio) ? op.ratio : 1} step={0.01} min={0} onChange={(v) => set(i, 'ratio', v)} />
            <NumberField label="Fb" title="Self feedback 0..1" value={isNum(op.feedback) ? op.feedback : undefined} step={0.01} min={0} max={1} onChange={(v) => set(i, 'feedback', v)} />
            <IconButton icon="x" title="Remove operator" size={12} onClick={() => onChange(ops.filter((_, j) => j !== i))} />
          </div>
          <div className="snd-grid2">
            <span className="snd-mini-label">Index</span>
            <ParamField value={op.index} step={0.1} min={0} placeholder={1} vars={vars} onChange={(v) => set(i, 'index', v)} />
            <span className="snd-mini-label">Env</span>
            <ParamField value={op.env} step={0.05} min={0} placeholder={1} vars={vars} kinds={['number', 'env', 'adsr', 'lfo']} onChange={(v) => set(i, 'env', v)} />
          </div>
        </div>
      ))}
      <div className="snd-row">
        <IconButton icon="plus" title="Add operator" size={13} disabled={ops.length >= 8} onClick={() => onChange([...ops, { ratio: ops.length + 1, index: 1 }])} />
        <span className="snd-hint">{`${ops.length} operators`}</span>
      </div>
    </div>
  );
}

function PartialList({ value, onChange, vars }: { value: unknown; onChange: (v: unknown) => void; vars: Record<string, number> }) {
  const parts: Obj[] = Array.isArray(value) ? value.map((o) => (isObj(o) ? o : {})) : [];
  const set = (i: number, k: string, v: unknown) => onChange(parts.map((o, j) => (j === i ? withField(o, k, v) : o)));
  return (
    <div className="snd-list">
      {parts.map((p, i) => (
        <div key={i} className="snd-subcard">
          <div className="snd-row">
            <NumberField label="Ratio" value={isNum(p.ratio) ? p.ratio : i + 1} step={0.01} min={0} onChange={(v) => set(i, 'ratio', v)} />
            <NumberField label="dB" title="Partial gain (dB)" value={isNum(p.gain) ? p.gain : undefined} step={0.5} onChange={(v) => set(i, 'gain', v)} />
            <NumberField label="Det" title="Detune (cents)" value={isNum(p.detune) ? p.detune : undefined} step={1} onChange={(v) => set(i, 'detune', v)} />
            <IconButton icon="x" title="Remove partial" size={12} onClick={() => onChange(parts.filter((_, j) => j !== i))} />
          </div>
          {p.env !== undefined && <ParamField value={p.env} step={0.05} vars={vars} onChange={(v) => set(i, 'env', v)} />}
          {p.env === undefined && (
            <button type="button" className="snd-link" onClick={() => set(i, 'env', { env: [[0, 0], [0.01, 1], [1, 0, 'exp']] })}>
              + envelope
            </button>
          )}
        </div>
      ))}
      <div className="snd-row">
        <IconButton icon="plus" title="Add partial" size={13} onClick={() => onChange([...parts, { ratio: parts.length + 1, gain: -6 * parts.length }])} />
        <span className="snd-hint">{parts.length ? `${parts.length} partials (override spectrum)` : 'no partials — uses spectrum'}</span>
      </div>
    </div>
  );
}

function SpectrumField({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
  const inharm = isObj(value) && isNum(value.inharmonic);
  const mode = typeof value === 'string' ? value : inharm ? 'inharmonic' : value === undefined ? '' : 'custom';
  return (
    <div className="snd-row">
      <SelectField
        value={mode}
        options={[
          { value: '', label: 'None' },
          { value: 'harmonic', label: 'Harmonic (saw-like)' },
          { value: 'odd', label: 'Odd (square-like)' },
          { value: 'bell', label: 'Bell' },
          { value: 'inharmonic', label: 'Inharmonic (stiff string)' },
          ...(mode === 'custom' ? [{ value: 'custom', label: 'Custom' }] : []),
        ]}
        onChange={(m) => onChange(m === '' ? undefined : m === 'inharmonic' ? { inharmonic: 0.001 } : m === 'custom' ? value : m)}
      />
      {inharm && (
        <NumberField label="B" title="Inharmonicity coefficient" value={(value as { inharmonic: number }).inharmonic} step={0.0005} min={0} onChange={(b) => onChange({ inharmonic: b })} />
      )}
    </div>
  );
}

function InstrumentRef({ value, onChange, doc }: { value: unknown; onChange: (v: unknown) => void; doc: unknown }) {
  const names = instrumentNames(doc);
  if (isObj(value)) {
    return (
      <div className="snd-list">
        <span className="snd-hint">Inline instrument definition</span>
        <JsonField value={value} onChange={onChange} rows={3} />
        <button type="button" className="snd-link" onClick={() => onChange(names[0] ?? '')}>
          use a named instrument instead
        </button>
      </div>
    );
  }
  return (
    <div className="snd-row">
      <SelectField
        value={typeof value === 'string' && value !== '' ? value : undefined}
        placeholder={names.length ? 'Choose…' : 'No instruments in this document'}
        options={names}
        onChange={(v) => onChange(v)}
      />
      <IconButton icon="data" title="Define inline (JSON)" size={13} onClick={() => onChange({ type: 'soundfont', program: 0 })} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Source editor                                                       */

/**
 * Editor for one `source` object: type picker plus the typed fields of that
 * source type (engine/docs/systems/sound.md "Sources"). Reused by the
 * instrument editor (synth patches, drum pads).
 */
export function SourceEditor({
  value,
  onChange,
  doc,
  depth = 0,
  duration,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  doc?: unknown;
  depth?: number;
  duration?: number;
}) {
  const src: Obj = isObj(value) ? value : {};
  const type = typeof src.type === 'string' ? src.type : '';
  const known = (SOURCE_TYPES as readonly string[]).includes(type);
  const vars = docVariables(doc);

  return (
    <div className="snd-source">
      <PropRow label="Type">
        <SelectField
          value={type || undefined}
          placeholder="Choose a source…"
          options={SOURCE_TYPES.filter((t) => depth < 2 || t !== 'mix').map((t) => ({ value: t, label: SOURCE_LABELS[t] }))}
          onChange={(t) => onChange(changeSourceType(src, t as SourceType))}
        />
      </PropRow>
      {known ? (
        <SchemaFields
          obj={src}
          fields={SOURCE_FIELDS[type as SourceType]}
          vars={vars}
          duration={duration}
          onChange={onChange}
          skip={['type', 'seed']}
          renderCustom={(f) => {
            const set = (v: unknown) => onChange(withField(src, f.key, v));
            switch (f.key) {
              case 'operators':
                return <OperatorList value={src.operators} vars={vars} onChange={set} />;
              case 'partials':
                return <PartialList value={src.partials} vars={vars} onChange={set} />;
              case 'spectrum':
                return <SpectrumField value={src.spectrum} onChange={set} />;
              case 'instrument':
                return <InstrumentRef value={src.instrument} doc={doc} onChange={set} />;
              case 'notes':
                return <NotesTable notes={Array.isArray(src.notes) ? src.notes : []} onChange={set} />;
              case 'voice':
                return <VoicePicker value={typeof src.voice === 'string' ? src.voice : undefined} onChange={set} />;
              case 'layers':
                return <LayerList value={src.layers} doc={doc} depth={depth + 1} onChange={set} />;
              default:
                return undefined;
            }
          }}
        />
      ) : (
        type && (
          <PropRow label="Spec">
            <JsonField value={src} onChange={onChange} rows={3} />
          </PropRow>
        )
      )}
      {known && (
        <PropRow label="Seed" hint="Per-source random seed (omit = layer/document seed)">
          <NumberField value={isNum(src.seed) ? src.seed : undefined} integer onChange={(v) => onChange(withField(src, 'seed', v))} />
        </PropRow>
      )}
    </div>
  );
}

/** Nested layers of a `mix` source (groups). */
function LayerList({ value, onChange, doc, depth }: { value: unknown; onChange: (v: unknown) => void; doc: unknown; depth: number }) {
  const layers: Obj[] = Array.isArray(value) ? value.map((l) => (isObj(l) ? l : {})) : [];
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="snd-list">
      {layers.map((l, i) => (
        <div key={i} className="snd-subcard">
          <div className="snd-row">
            <button type="button" className="snd-link" onClick={() => setOpen(open === i ? null : i)}>
              {`${open === i ? '▾' : '▸'} ${typeof l.name === 'string' ? l.name : `Layer ${i + 1}`}`}
              {isObj(l.source) && typeof l.source.type === 'string' ? ` · ${l.source.type}` : ''}
            </button>
            <span className="snd-spacer" />
            <IconButton icon="x" title="Remove layer" size={12} onClick={() => onChange(layers.filter((_, j) => j !== i))} />
          </div>
          {open === i && (
            <LayerInspector value={l} doc={doc} depth={depth} onChange={(nl) => onChange(layers.map((x, j) => (j === i ? nl : x)))} />
          )}
        </div>
      ))}
      <div className="snd-row">
        <IconButton
          icon="plus"
          title="Add nested layer"
          size={13}
          onClick={() => {
            onChange([...layers, { name: `layer ${layers.length + 1}`, source: { type: 'osc', wave: 'sine', note: 'A4' } }]);
            setOpen(layers.length);
          }}
        />
        <span className="snd-hint">{`${layers.length} nested layers`}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sends                                                               */

function SendsField({ value, onChange, doc }: { value: unknown; onChange: (v: unknown) => void; doc: unknown }) {
  const sends: Obj = isObj(value) ? value : {};
  const buses = returnNames(doc);
  const unused = buses.filter((b) => !(b in sends));
  const set = (k: string, v: number | undefined) => {
    const next = withField(sends, k, v);
    onChange(Object.keys(next).length ? next : undefined);
  };
  return (
    <div className="snd-list">
      {Object.entries(sends).map(([bus, db]) => (
        <div key={bus} className="snd-row">
          <span className={`snd-chip mono${buses.includes(bus) ? '' : ' is-warn'}`} title={buses.includes(bus) ? 'Return bus' : 'No such return in master.returns'}>
            {bus}
          </span>
          <NumberField label="dB" value={isNum(db) ? db : 0} step={0.5} max={12} onChange={(v) => set(bus, v)} />
          <IconButton icon="x" title="Remove send" size={12} onClick={() => set(bus, undefined)} />
        </div>
      ))}
      {buses.length === 0 ? (
        <span className="snd-hint">Add return buses in Master → returns to send to them.</span>
      ) : (
        unused.length > 0 && (
          <SelectField value={undefined} placeholder="Add send…" options={unused} onChange={(b) => b && set(b, -12)} />
        )
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Layer                                                               */

/**
 * Inspector for one layer (`doc.layers[i]` or a nested group layer): name,
 * timing, gain/pan, mute, source (type + typed fields), amp and pitch
 * (time-varying), effects and sends, with a solo preview.
 */
export function LayerInspector({
  value,
  onChange,
  doc,
  depth = 0,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  doc?: unknown;
  depth?: number;
}) {
  const layer: Obj = isObj(value) ? value : {};
  const vars = docVariables(doc);
  const set = (k: string, v: unknown) => onChange(withField(layer, k, v));
  const duration = isObj(doc) && isNum(doc.duration) ? doc.duration : undefined;
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = async () => {
    if (playing) {
      stopPreview();
      setPlaying(false);
      return;
    }
    setPlaying(true);
    setError(null);
    try {
      const body: Obj = { kind: 'sfx', layers: [{ ...layer, start: 0, mute: false }] };
      if (duration !== undefined) body.duration = duration;
      await previewDoc(previewDocument(typeof layer.name === 'string' ? layer.name : 'Layer preview', body, doc));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPlaying(false);
    }
  };

  return (
    <div className="snd-editor">
      <div className="snd-row">
        <TextField value={typeof layer.name === 'string' ? layer.name : ''} placeholder="Layer name" onChange={(t) => set('name', t || undefined)} />
        <Toggle checked={layer.mute === true} label="Mute" onChange={(m) => set('mute', m ? true : undefined)} />
        <IconButton icon={playing ? 'stop' : 'play'} title={playing ? 'Stop' : 'Preview this layer solo'} size={14} onClick={() => void preview()} />
      </div>
      {error && <span className="snd-hint is-error">{error}</span>}
      <PropRow label="Start" hint="Start time (s)">
        <ParamField value={layer.start} step={0.01} min={0} placeholder={0} vars={vars} kinds={['number', 'random', 'expr']} onChange={(v) => set('start', v)} />
      </PropRow>
      <PropRow label="Gain" hint="dB">
        <ParamField value={layer.gain} step={0.5} placeholder={0} vars={vars} duration={duration} onChange={(v) => set('gain', v)} />
      </PropRow>
      <PropRow label="Pan" hint="-1 left .. 1 right">
        <ParamField value={layer.pan} step={0.05} min={-1} max={1} placeholder={0} vars={vars} duration={duration} onChange={(v) => set('pan', v)} />
      </PropRow>

      <Card title="Source" subtitle={isObj(layer.source) && typeof layer.source.type === 'string' ? layer.source.type : 'none'}>
        <SourceEditor value={layer.source} doc={doc} depth={depth} duration={duration} onChange={(v) => set('source', v)} />
      </Card>

      <Card title="Amp & pitch">
        <PropRow label="Amp" hint="Linear 0..1 multiplier over time (envelope, ADSR per note, LFO…)">
          <ParamField value={layer.amp} step={0.05} min={0} placeholder={1} vars={vars} duration={duration ?? 1} onChange={(v) => set('amp', v)} />
        </PropRow>
        <PropRow label="Pitch" hint="Semitone offset (time-varying allowed)">
          <ParamField value={layer.pitch} step={0.5} placeholder={0} vars={vars} duration={duration ?? 1} onChange={(v) => set('pitch', v)} />
        </PropRow>
        <PropRow label="Seed" hint="Layer random seed (omit = document seed)">
          <NumberField value={isNum(layer.seed) ? layer.seed : undefined} integer onChange={(v) => set('seed', v)} />
        </PropRow>
      </Card>

      <Card title="Effects" subtitle={Array.isArray(layer.effects) ? String(layer.effects.length) : '0'}>
        <EffectsEditor value={layer.effects} doc={doc} onChange={(v) => set('effects', v)} />
      </Card>

      <Card title="Sends" defaultOpen={isObj(layer.send)}>
        <SendsField value={layer.send} doc={doc} onChange={(v) => set('send', v)} />
      </Card>
    </div>
  );
}
